import { useEffect, useState } from "react";
import { app, pages, meeting } from "@microsoft/teams-js";
import { Whiteboard } from "./Whiteboard";

type Route = "config" | "stage" | "sidepanel" | "browser";

function detectRoute(): Route {
  const p = window.location.pathname.toLowerCase();
  if (p.startsWith("/config")) return "config";
  if (p.startsWith("/stage")) return "stage";
  if (p.startsWith("/sidepanel")) return "sidepanel";
  return "browser";
}

export function App() {
  const route = detectRoute();
  const [inTeams, setInTeams] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        await app.initialize();
        setInTeams(true);
        app.notifyAppLoaded();
        app.notifySuccess();
      } catch {
        // not in teams
      } finally {
        setReady(true);
      }
    })();
  }, []);

  if (!ready) return <Splash text="loading…" />;
  if (route === "config") return <ConfigPage />;
  if (route === "stage" || route === "sidepanel") {
    return (
      <div style={{ position: "relative" }}>
        <Whiteboard inTeams={inTeams} />
        {route === "sidepanel" && inTeams && <ShareBar />}
      </div>
    );
  }
  return <BrowserHome />;
}

function ShareBar() {
  const [msg, setMsg] = useState<string>("");
  const shareToStage = async () => {
    const url = `${window.location.origin}/stage`;
    meeting.shareAppContentToStage((err, ok) => {
      if (err) setMsg(`Error: ${err.message ?? JSON.stringify(err)}`);
      else setMsg(ok ? "Shared to stage ✅" : "Share returned false");
    }, url);
  };
  return (
    <div style={{ position: "fixed", bottom: 12, right: 12, display: "flex", gap: 8, alignItems: "center", background: "white", padding: "6px 10px", borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.15)", zIndex: 100 }}>
      {msg && <span style={{ fontSize: 12, opacity: 0.7 }}>{msg}</span>}
      <button onClick={shareToStage} style={{ padding: "6px 12px", background: "#5b5fc7", color: "white", border: 0, borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
        Share to stage
      </button>
    </div>
  );
}

function ConfigPage() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    (async () => {
      await app.initialize();
      pages.config.registerOnSaveHandler((saveEvent) => {
        const base = window.location.origin;
        pages.config.setConfig({
          suggestedDisplayName: "Live Whiteboard",
          contentUrl: `${base}/sidepanel`,
          websiteUrl: base,
          entityId: "live-whiteboard"
        }).then(() => saveEvent.notifySuccess());
      });
      pages.config.setValidityState(true);
      setReady(true);
    })();
  }, []);
  return <Splash text={ready ? "Ready. Click Save to add this tab." : "Loading…"} title="Live Whiteboard — configure" />;
}

function BrowserHome() {
  return (
    <Splash title="Teams Stage Live Whiteboard">
      Open inside a Teams meeting to use it.<br/>
      <ul>
        <li><a href="/sidepanel">/sidepanel</a> — try the whiteboard locally (test mode)</li>
        <li><a href="/stage">/stage</a> — stage view</li>
        <li><a href="/config">/config</a> — config page</li>
      </ul>
    </Splash>
  );
}

function Splash({ title = "Teams Stage", text, children }: { title?: string; text?: string; children?: React.ReactNode }) {
  return (
    <div style={{ fontFamily: "Segoe UI, system-ui, sans-serif", padding: 24, color: "#1f1f1f", background: "#f5f5f5", minHeight: "100vh" }}>
      <h2>{title}</h2>
      {text && <p>{text}</p>}
      {children}
    </div>
  );
}
